import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDuration, initials, type Period } from '@coachrex/shared';
import { useCoachingDetail, useCoachingList } from '../lib/api';
import { useRepFilter } from '../lib/repFilter';
import { scoreColor, stamp } from '../lib/format';
import { Chip, ErrorBox, PageHeader, Segmented, Skeleton } from '../components/ui';
import { CoachingObjections } from './CoachingObjections';
import './coaching.css';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
];

function speed(min: number | null): string {
  if (min == null) return '—';
  if (min < 60) return `${min}m`;
  if (min < 1440) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / 1440)}d`;
}

/** Handoff screen 05. */
export function Coaching() {
  const [params, setParams] = useSearchParams();
  const period = (PERIODS.some((p) => p.value === params.get('period')) ? params.get('period') : 'week') as Period;
  const view = params.get('view') === 'objections' ? 'objections' : 'reps';
  const list = useCoachingList(period);
  const repFilter = useRepFilter();
  const reps = list.data?.reps ?? [];
  // The shared rep filter picks the rep when it names one on the list; otherwise the first rep.
  const selected = params.get('rep') ?? (reps.some((r) => r.rep.id === repFilter.value) ? repFilter.value : reps[0]?.rep.id ?? null);
  const detail = useCoachingDetail(selected, period);
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  useEffect(() => {
    if (params.get('rep') && reps.length && !reps.some((r) => r.rep.id === params.get('rep'))) set('rep', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reps]);

  return (
    <div className="page">
      <PageHeader
        title="Coaching"
        subtitle={view === 'reps' ? 'Scorecards and specific moments to review with each rep, from their analyzed calls.' : 'What leads push back on, from every analyzed call.'}
        actions={
          <Segmented
            label="View"
            options={[
              { value: 'reps', label: 'Reps' },
              { value: 'objections', label: 'Objections' },
            ]}
            value={view}
            onChange={(v) => set('view', v === 'reps' ? null : v)}
          />
        }
      />
      {view === 'objections' ? (
        <CoachingObjections period={period} periods={PERIODS} onPeriod={(p) => set('period', p === 'week' ? null : p)} />
      ) : (
      <div className="coach-cols">
        <section className="card coach-reps">
          {list.error ? (
            <ErrorBox error={list.error} />
          ) : list.isLoading ? (
            [0, 1, 2, 3].map((i) => <Skeleton key={i} height={46} />)
          ) : reps.length === 0 ? (
            <span className="card-meta" style={{ padding: 10 }}>
              No calls in this period yet.
            </span>
          ) : (
            reps.map((r) => (
              <button key={r.rep.id} className={`coach-rep${r.rep.id === selected ? ' is-selected' : ''}`} onClick={() => set('rep', r.rep.id)}>
                <span className="team-avatar">{initials(r.rep.name).slice(0, 2)}</span>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, textAlign: 'left' }}>
                  <span style={{ fontWeight: 600 }}>{r.rep.name}</span>
                  <span className="card-meta">
                    {r.moments} moment{r.moments === 1 ? '' : 's'} to review
                  </span>
                </span>
                <span style={{ fontWeight: 600, color: r.score != null ? scoreColor(r.score) : 'var(--faint)' }}>{r.score ?? '—'}</span>
              </button>
            ))
          )}
        </section>

        <div className="coach-main">
          {!selected ? null : detail.error ? (
            <ErrorBox error={detail.error} />
          ) : !detail.data ? (
            <>
              <Skeleton height={96} />
              <Skeleton height={220} />
            </>
          ) : (
            <Detail d={detail.data} period={period} onPeriod={(p) => set('period', p === 'week' ? null : p)} />
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function Detail({ d, period, onPeriod }: { d: NonNullable<ReturnType<typeof useCoachingDetail>['data']>; period: Period; onPeriod: (p: Period) => void }) {
  const scored = d.series.filter((s) => s.score != null);
  const first = scored[0]?.score;
  const last = scored[scored.length - 1]?.score;
  const lastIdx = d.series.map((s) => s.score != null).lastIndexOf(true);
  const per = period === 'week' ? 'this week' : period === 'month' ? 'this month' : 'this quarter';
  return (
    <>
      <section className="card coach-head">
        <span className="coach-avatar">{initials(d.rep.name).slice(0, 2)}</span>
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="h2">{d.rep.name}</span>
          <span className="card-meta" style={{ fontSize: 13 }}>
            {d.role === 'manager' ? 'Manager' : 'Rep'} · {d.calls.toLocaleString('en-US')} calls {per} · {d.bookedRate == null ? '—' : `${d.bookedRate}%`} booked · {speed(d.speedToLeadMin)} speed to lead
          </span>
        </span>
        <span style={{ textAlign: 'right' }}>
          <span className="coach-score" style={{ color: d.score != null ? scoreColor(d.score) : 'var(--faint)' }}>
            {d.score ?? '—'}
          </span>
          {d.trend != null && (
            <span className="card-meta" style={{ display: 'block', color: d.trend >= 0 ? 'var(--score-good)' : 'var(--score-bad)', fontWeight: 600 }}>
              {d.trend >= 0 ? '▲' : '▼'} {Math.abs(d.trend)} vs previous
            </span>
          )}
        </span>
      </section>

      <section className="card stack-12">
        <div className="card-head" style={{ alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="card-title">Rep score over time</span>
            {first != null && last != null && scored.length > 1 && (
              <span style={{ fontWeight: 600, fontSize: 13, color: last >= first ? 'var(--score-good)' : 'var(--score-bad)' }}>
                {first} → {last}
              </span>
            )}
          </span>
          <Segmented label="Period" options={PERIODS} value={period} onChange={onPeriod} />
        </div>
        <div className="coach-bars">
          {d.series.map((s, i) => (
            <span key={i} className="coach-bar" title={`${s.label}: ${s.score ?? 'no scored calls'}`}>
              <span className={s.score == null ? 'is-empty' : i === lastIdx ? 'is-last' : undefined} style={{ height: `${s.score ?? 3}%` }} />
              <span className="coach-bar__label">{s.label}</span>
            </span>
          ))}
        </div>
        {scored.length === 0 && <span className="card-meta">No scored calls in this period yet.</span>}
      </section>

      <div className="coach-grid">
        <section className="card stack-12">
          <span className="card-title">Pass rate by criterion</span>
          {d.criteria.length === 0 && <span className="card-meta">No analyzed calls in this period.</span>}
          {d.criteria.map((c) => (
            <div key={c.name} className="crit-bar">
              <span className="crit-bar__top">
                <span>{c.name}</span>
                <b style={{ color: scoreColor(c.passRate) }}>{c.passRate}%</b>
              </span>
              <span className="bar">
                <span style={{ width: `${c.passRate}%`, background: scoreColor(c.passRate) }} />
              </span>
            </div>
          ))}
        </section>

        <section className="card stack-12">
          <span className="card-title">Moments to review</span>
          {d.moments.length === 0 && <span className="card-meta">Nothing flagged in this period.</span>}
          {d.moments.map((m, i) => (
            <div key={`${m.touchId}-${i}`} className="moment">
              <Chip tone={m.tone} size="sm">
                {m.flag}
              </Chip>
              <span className="card-meta">
                {m.leadName} · {stamp(m.at, d.timeZone)} · at {formatDuration(m.t)}
              </span>
              <span style={{ fontSize: 13 }}>{m.text}</span>
              <Link to={`/conversations/${m.leadId}?call=${m.touchId}&t=${m.t}`} style={{ fontSize: 12.5, fontWeight: 500 }}>
                Review call →
              </Link>
            </div>
          ))}
        </section>
      </div>

      <section className="card stack-12">
        <span className="card-title">Objection handling</span>
        {d.objections.length === 0 ? (
          <span className="card-meta">No objections tagged on this rep’s calls in this period.</span>
        ) : (
          <>
            <div className="list-head obj-grid" style={{ padding: '0 0 8px' }}>
              <span>Objection</span>
              <span style={{ textAlign: 'right' }}>Heard</span>
              <span>Handled well</span>
            </div>
            {d.objections.map((o) => (
              <div key={o.objection} className="obj-grid obj-row">
                <span>{o.objection}</span>
                <span style={{ textAlign: 'right' }}>{o.heard}</span>
                <span className="obj-bar">
                  <span className="bar">
                    <span style={{ width: `${o.handledPct}%`, background: scoreColor(o.handledPct) }} />
                  </span>
                  <b style={{ color: scoreColor(o.handledPct) }}>{o.handledPct}%</b>
                </span>
              </div>
            ))}
            <span className="card-meta">{d.handledRule}</span>
          </>
        )}
      </section>
    </>
  );
}
